import { create } from "zustand";
import { persist } from "zustand/middleware";

import type {
  DrawingVisionResult,
  OpciDioStatus,
  RecognizedDocument,
  SituacijaReport,
} from "@/api/schemas";

/** B1 — a proposed fact the architect confirmed (propose-then-confirm). Only
 *  these become grounded qualitative facts in docgen; carries the drawing type
 *  so the backend can attribute it. */
export interface ConfirmedNacrtFact {
  kljuc: string;
  vrijednost: string;
  tip: string;
  doc_type: string;
  citat_vizualni: string;
}

interface IntakeEntry {
  /** Parsed situacija report — the exact ``situacija`` payload the
   *  generate request forwards (one merge path, see DataPage). */
  situacija: SituacijaReport | null;
  /** Recognized opći-dio documents (sudski registar, HKA, posebni uvjeti…)
   *  accumulated from the upload panel — forwarded verbatim into
   *  /api/opci-dio/status and the generate request. */
  recognizedDocs: RecognizedDocument[];
  /** Last /api/opci-dio/status response — the completeness panel renders
   *  from this; refreshed whenever an intake source changes. */
  opciDioStatus: OpciDioStatus | null;
  /** B1 — per-drawing vision output (one entry per read nacrt). Forwarded as
   *  ``nacrt_opis`` in the generate request; the backend uses only ``opis_prose``
   *  (proposals live here only for the review UI). */
  nacrtVision: DrawingVisionResult[];
  /** B1 — the proposed facts the architect confirmed. Forwarded as
   *  ``nacrt_confirmed_facts``; only these reach the grounded layer. */
  nacrtConfirmedFacts: ConfirmedNacrtFact[];
}

/** Stable identity of a proposed/confirmed fact within a project. */
function factKey(docType: string, kljuc: string, vrijednost: string): string {
  return `${docType} ${kljuc} ${vrijednost}`;
}

interface IntakeStore {
  byProject: Record<string, IntakeEntry>;
  setSituacija: (projectId: string, report: SituacijaReport | null) => void;
  setOpciDioStatus: (projectId: string, status: OpciDioStatus | null) => void;
  addRecognizedDoc: (projectId: string, doc: RecognizedDocument) => void;
  removeRecognizedDoc: (projectId: string, index: number) => void;
  /** Manual classification override — the user stays the gate on a wrong
   *  LLM classification. ``target_slot`` is cleared so the backend re-derives
   *  the slot from its own DOCUMENT_TYPES map for the corrected type. */
  overrideDocType: (projectId: string, index: number, documentType: string) => void;
  /** B1 — store (or replace) a drawing's vision output by doc_id. */
  setNacrtVision: (projectId: string, result: DrawingVisionResult) => void;
  removeNacrtVision: (projectId: string, docId: string) => void;
  /** B1 — toggle a proposed fact's confirmed state (propose-then-confirm). */
  confirmNacrtFact: (projectId: string, fact: ConfirmedNacrtFact) => void;
  unconfirmNacrtFact: (
    projectId: string,
    docType: string,
    kljuc: string,
    vrijednost: string,
  ) => void;
  isNacrtFactConfirmed: (
    projectId: string | undefined,
    docType: string,
    kljuc: string,
    vrijednost: string,
  ) => boolean;
  get: (projectId: string | undefined) => IntakeEntry;
}

const EMPTY: IntakeEntry = {
  situacija: null,
  recognizedDocs: [],
  opciDioStatus: null,
  nacrtVision: [],
  nacrtConfirmedFacts: [],
};

function patch(
  s: IntakeStore,
  projectId: string,
  next: Partial<IntakeEntry>,
): Pick<IntakeStore, "byProject"> {
  return {
    byProject: {
      ...s.byProject,
      [projectId]: { ...(s.byProject[projectId] ?? EMPTY), ...next },
    },
  };
}

/**
 * Intake state per project — what Step 2 has collected beyond the form
 * itself (situacija report, recognized opći-dio documents, opći-dio
 * status). Lives in a shared store (not panel-local state) so the generate
 * request and the completeness panel read the SAME data the panels produced.
 * Persisted: the source files are gone after a refresh, so "recompute it"
 * isn't an option — without persistence a hard refresh silently emptied
 * the readiness panel and the status strip.
 */
export const useIntakeStore = create<IntakeStore>()(
  persist(
    (set, get) => ({
      byProject: {},
  setSituacija: (projectId, report) =>
    set((s) => patch(s, projectId, { situacija: report })),
  setOpciDioStatus: (projectId, status) =>
    set((s) => patch(s, projectId, { opciDioStatus: status })),
  addRecognizedDoc: (projectId, doc) =>
    set((s) =>
      patch(s, projectId, {
        recognizedDocs: [...(s.byProject[projectId]?.recognizedDocs ?? []), doc],
      }),
    ),
  removeRecognizedDoc: (projectId, index) =>
    set((s) =>
      patch(s, projectId, {
        recognizedDocs: (s.byProject[projectId]?.recognizedDocs ?? []).filter(
          (_, i) => i !== index,
        ),
      }),
    ),
  overrideDocType: (projectId, index, documentType) =>
    set((s) =>
      patch(s, projectId, {
        recognizedDocs: (s.byProject[projectId]?.recognizedDocs ?? []).map(
          (d, i) =>
            i === index
              ? {
                  ...d,
                  document_type: documentType,
                  target_slot: null,
                  classification_confidence: 1.0, // human said so
                  _user_overridden: true,
                }
              : d,
        ),
      }),
    ),
  setNacrtVision: (projectId, result) =>
    set((s) => {
      const docId = String(result.provenance?.doc_id ?? "");
      const prev = s.byProject[projectId]?.nacrtVision ?? [];
      // Replace an existing read of the same drawing; append otherwise.
      const kept = docId
        ? prev.filter((r) => String(r.provenance?.doc_id ?? "") !== docId)
        : prev;
      return patch(s, projectId, { nacrtVision: [...kept, result] });
    }),
  removeNacrtVision: (projectId, docId) =>
    set((s) =>
      patch(s, projectId, {
        nacrtVision: (s.byProject[projectId]?.nacrtVision ?? []).filter(
          (r) => String(r.provenance?.doc_id ?? "") !== docId,
        ),
      }),
    ),
  confirmNacrtFact: (projectId, fact) =>
    set((s) => {
      const prev = s.byProject[projectId]?.nacrtConfirmedFacts ?? [];
      const key = factKey(fact.doc_type, fact.kljuc, fact.vrijednost);
      if (prev.some((f) => factKey(f.doc_type, f.kljuc, f.vrijednost) === key)) {
        return {}; // already confirmed — no-op
      }
      return patch(s, projectId, { nacrtConfirmedFacts: [...prev, fact] });
    }),
  unconfirmNacrtFact: (projectId, docType, kljuc, vrijednost) =>
    set((s) => {
      const key = factKey(docType, kljuc, vrijednost);
      return patch(s, projectId, {
        nacrtConfirmedFacts: (
          s.byProject[projectId]?.nacrtConfirmedFacts ?? []
        ).filter((f) => factKey(f.doc_type, f.kljuc, f.vrijednost) !== key),
      });
    }),
  isNacrtFactConfirmed: (projectId, docType, kljuc, vrijednost) => {
    if (!projectId) return false;
    const key = factKey(docType, kljuc, vrijednost);
    return (get().byProject[projectId]?.nacrtConfirmedFacts ?? []).some(
      (f) => factKey(f.doc_type, f.kljuc, f.vrijednost) === key,
    );
  },
      get: (projectId) =>
        (projectId ? get().byProject[projectId] : undefined) ?? EMPTY,
    }),
    { name: "permitpilot.intake.v1" },
  ),
);
